/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */

import { createApi } from "@reduxjs/toolkit/query/react";

import { getCSRFTokenHeader } from "common/hooks";
import { convertObjectKeysToSnakeCase, transformResponse } from "common/util";
import Absence from "pages/Absence/types/Absence";
import { dynamicBaseQuery } from "store/api/utils/dynamicBaseQuery";
import { RequestMethod } from "store/types";

export type FileAttachment = {
    fileAttachmentId: string;
    filename: string;
};

interface RequestPayload {
    url: string;
    data: unknown;
}

type AbsenceCreate = {
    startTs: number;
    endTs: number;
    absenceType: string;
    driverNote?: string;
    fileAttachments?: FileAttachment[];
};

export type AbsenceCreatePayload = {
    url: string;
    data: AbsenceCreate;
};

interface AbsenceResponse {
    absences: Absence[];
}

type AttachmentUploadUrlResponse = {
    file_attachment_id: string;
    upload_url: string;
    payload: Record<string, string>;
};

export const absencesApiSlice = createApi({
    baseQuery: dynamicBaseQuery,
    reducerPath: "absences",
    refetchOnMountOrArgChange: true,
    tagTypes: ["Absences"],
    endpoints: (builder) => ({
        getAbsences: builder.query<AbsenceResponse, string>({
            providesTags: ["Absences"],
            query: (url) => url,
            transformResponse: (response: AbsenceResponse) => ({
                absences: transformResponse(response.absences),
            }),
        }),
        applyAbsence: builder.mutation<void, AbsenceCreatePayload>({
            invalidatesTags: ["Absences"],
            query: ({ url, data }) => ({
                body: convertObjectKeysToSnakeCase(data),
                headers: {
                    ...getCSRFTokenHeader(),
                },
                method: RequestMethod.POST,
                responseType: "text",
                url,
            }),
        }),
        cancelAbsence: builder.mutation<void, RequestPayload>({
            invalidatesTags: ["Absences"],
            query: ({ url, data }) => ({
                body: data,
                headers: {
                    ...getCSRFTokenHeader(),
                },
                method: RequestMethod.PUT,
                responseType: "text",
                url,
            }),
        }),
        getDownloadAttachmentLink: builder.query<{ downloadLink: string }, string>({
            query: (url) => url,
            transformResponse: (response: { downloadLink: string }) => transformResponse(response),
        }),
        getAttachmentUploadUrl: builder.query<AttachmentUploadUrlResponse, any>({
            query: (url) => url,
            transformResponse: (response: AttachmentUploadUrlResponse) => response,
        }),
        uploadAttachmentFile: builder.mutation<any, any>({
            queryFn: async ({ url, formData }) => {
                try {
                    const response = await fetch(url, {
                        method: RequestMethod.POST,
                        body: formData,
                    });

                    if (!response.ok) {
                        return { error: { status: response.status, data: await response.text() } };
                    }

                    return { data: await response.text() };
                } catch (error) {
                    return { error: { status: "FETCH_ERROR", data: (error as Error).message } };
                }
            },
        }),
    }),
});

export const {
    useGetAbsencesQuery,
    useCancelAbsenceMutation,
    useApplyAbsenceMutation,
    useLazyGetDownloadAttachmentLinkQuery,
    useLazyGetAttachmentUploadUrlQuery,
    useUploadAttachmentFileMutation,
} = absencesApiSlice;
